import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { ImageLightbox } from '../components/ImageLightbox';
import { CheckCircle, Play, Pause, ShieldCheck } from 'lucide-react';

/* Compact video player card — supports mp4 (poster + click) OR Loom (static thumb + click-to-play) */
const VideoCard = ({ src, poster, loomSrc, aspect = 'aspect-video', testId }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loomOpened, setLoomOpened] = useState(false);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const loomId = loomSrc ? loomSrc.split('/').pop().split('?')[0] : null;
  const loomThumb = loomId ? `/loom-thumbs/${loomId}.jpg` : null;

  // --- Loom embed: click-to-play with static thumbnail ---
  if (loomSrc) {
    return loomOpened ? (
      <div
        className={`relative w-full ${aspect} rounded-[16px] overflow-hidden border-2 md:border-4 border-[#C9A55A]/35 bg-[#220410] shadow-xl`}
        data-testid={testId}
      >
        <iframe
          src={`${loomSrc}?autoplay=1`}
          title={testId || 'video'}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
          className="absolute inset-0 w-full h-full"
        />
      </div>
    ) : (
      <button
        type="button"
        onClick={() => setLoomOpened(true)}
        aria-label="Play video"
        className={`relative w-full ${aspect} rounded-[16px] overflow-hidden border-2 md:border-4 border-[#C9A55A]/35 bg-[#220410] shadow-xl cursor-pointer group block`}
        data-testid={testId}
      >
        <img
          src={loomThumb}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-[#2C0616] rounded-full p-3 md:p-5 shadow-2xl transition-transform duration-200 group-hover:scale-110">
            <Play className="w-6 h-6 md:w-9 md:h-9 text-[#F5F0E8] ml-0.5" fill="#1F0410" />
          </div>
        </div>
      </button>
    );
  }

  // --- mp4 or empty placeholder ---
  return (
    <div
      className={`relative w-full ${aspect} rounded-[16px] overflow-hidden border-2 md:border-4 border-[#C9A55A]/35 cursor-pointer group bg-[#220410] shadow-xl`}
      onClick={src ? toggle : undefined}
      data-testid={testId}
    >
      {src ? (
        <>
          <video
            ref={ref}
            poster={poster}
            playsInline
            preload="metadata"
            onEnded={() => setPlaying(false)}
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={src} type="video/mp4" />
          </video>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              className={`bg-[#2C0616] rounded-full p-3 md:p-5 shadow-2xl transition-all duration-300 ${
                playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100 scale-100 group-hover:scale-110'
              }`}
            >
              {playing
                ? <Pause className="w-6 h-6 md:w-9 md:h-9 text-[#F5F0E8]" fill="#1F0410" />
                : <Play className="w-6 h-6 md:w-9 md:h-9 text-[#F5F0E8] ml-0.5" fill="#1F0410" />}
            </div>
          </div>
        </>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <Play className="w-10 h-10 md:w-14 md:h-14 text-[#F5F0E8]/38 mb-2" />
          <p className="font-sans text-sm md:text-base text-[#F5F0E8]/38 italic">
            Video coming soon
          </p>
        </div>
      )}
    </div>
  );
};

/* Always-visible FAQ tile — static Loom thumbnail, click-to-play with sound */
const FaqTile = ({ index, question, src, loomSrc, paddingBottom = '56.25%', slug, autoOpen = false }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [loomOpened, setLoomOpened] = useState(autoOpen);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  // Extract Loom video ID from https://www.loom.com/embed/{id}
  const loomId = loomSrc ? loomSrc.split('/').pop().split('?')[0] : null;
  const loomThumb = loomId ? `/loom-thumbs/${loomId}.jpg` : null;

  return (
    <div className="space-y-3 scroll-mt-24" id={slug} data-testid={`faq-${index}`}>
      <h3
        className="text-center font-sans text-base md:text-xl lg:text-2xl text-[#F5F0E8] leading-snug px-1"
        style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
      >
        {question}
      </h3>

      {loomSrc ? (
        loomOpened ? (
          <div
            className="relative w-full rounded-[16px] overflow-hidden border-2 border-[#C9A55A]/35 bg-[#220410] shadow-md"
            style={{ paddingBottom, height: 0 }}
          >
            <iframe
              src={`${loomSrc}?autoplay=1`}
              title={question}
              frameBorder="0"
              allow="autoplay; fullscreen"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setLoomOpened(true)}
            aria-label={`Play video: ${question}`}
            className="relative w-full rounded-[16px] overflow-hidden border-2 border-[#C9A55A]/35 bg-[#220410] shadow-md group block"
            style={{ paddingBottom, height: 0 }}
          >
            {loomThumb && (
              <img
                src={loomThumb}
                alt={question}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-[#2C0616] rounded-full p-3 md:p-4 shadow-2xl transition-transform duration-200 group-hover:scale-110">
                <Play className="w-6 h-6 md:w-8 md:h-8 text-[#F5F0E8] ml-0.5" fill="#1F0410" />
              </div>
            </div>
          </button>
        )
      ) : (
        <div
          className="relative w-full aspect-video rounded-[16px] overflow-hidden border-2 border-[#C9A55A]/35 bg-[#220410] cursor-pointer group"
          onClick={src ? toggle : undefined}
        >
          {src ? (
            <>
              <video
                ref={ref}
                playsInline
                preload="metadata"
                onEnded={() => setPlaying(false)}
                className="absolute inset-0 w-full h-full object-cover"
              >
                <source src={src} type="video/mp4" />
              </video>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className={`bg-[#2C0616] rounded-full p-2 md:p-3 shadow-lg transition-all ${playing ? 'opacity-0 group-hover:opacity-100' : ''}`}>
                  {playing
                    ? <Pause className="w-5 h-5 md:w-6 md:h-6 text-[#F5F0E8]" fill="#1F0410" />
                    : <Play className="w-5 h-5 md:w-6 md:h-6 text-[#F5F0E8] ml-0.5" fill="#1F0410" />}
                </div>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
              <Play className="w-8 h-8 text-[#F5F0E8]/38" />
              <p className="font-sans text-xs text-[#F5F0E8]/38 italic">Video coming soon</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* Vidalytics video embed — one instance per video id */
const VidalyticsEmbed = ({ embedId, testId }) => {
  const scriptInjected = useRef(false);
  const videoId = embedId.replace('vidalytics_embed_', '');

  useEffect(() => {
    if (scriptInjected.current) return;

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.text = `
      (function (v, i, d, a, l, y, t, c, s) {
        y='_'+d.toLowerCase();c=d+'L';if(!v[d]){v[d]={};}if(!v[c]){v[c]={};}if(!v[y]){v[y]={};}var vl='Loader',vli=v[y][vl],vsl=v[c][vl + 'Script'],vlf=v[c][vl + 'Loaded'],ve='Embed';
        if (!vsl){vsl=function(u,cb){
          if(t){cb();return;}s=i.createElement("script");s.type="text/javascript";s.async=1;s.src=u;
          if(s.readyState){s.onreadystatechange=function(){if(s.readyState==="loaded"||s.readyState=="complete"){s.onreadystatechange=null;vlf=1;cb();}};}else{s.onload=function(){vlf=1;cb();};}
          i.getElementsByTagName("head")[0].appendChild(s);
        };}
        vsl(l+'loader.min.js',function(){if(!vli){var vlc=v[c][vl];vli=new vlc();}vli.loadScript(l+'player.min.js',function(){var vec=v[d][ve];t=new vec();t.run(a);});});
      })(window, document, 'Vidalytics', '${embedId}', 'https://fast.vidalytics.com/embeds/85Ip_8hv/${videoId}/');
    `;

    document.body.appendChild(script);
    scriptInjected.current = true;

    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, [embedId, videoId]);

  return (
    <div
      id={embedId}
      style={{ width: '100%', position: 'relative', paddingTop: '56.25%' }}
      data-testid={testId}
    />
  );
};

const FAQ_ITEMS = [
  {
    slug: 'why-prima',
    question: 'What makes Prima Agency different?',
    loomSrc: 'https://www.loom.com/embed/e820a07be94d40eb984de341a75ddc66',
    paddingBottom: '75%',
  },
  {
    slug: 'what-we-do',
    question: 'What do you actually do for 50%?',
    loomSrc: 'https://www.loom.com/embed/940eaf1d8c944fef9e8fdd69c1268d2a',
    paddingBottom: '75%',
  },
  {
    slug: 'onboarding',
    question: 'What does the onboarding process look like?',
    loomSrc: 'https://www.loom.com/embed/237108fa6f904af093fbc7ceb065e3bf',
    paddingBottom: '56.25%',
  },
  {
    slug: 'content',
    question: 'How much content do you need to do?',
    loomSrc: 'https://www.loom.com/embed/3f92f1ecebf74d2db6798ba11a29b9ec',
    paddingBottom: '56.25%',
  },
  {
    slug: 'results',
    question: 'How quickly will I see results?',
    loomSrc: 'https://www.loom.com/embed/f167e884c13f4f2fa018400a9f60f01c',
    paddingBottom: '56.25%',
  },
  {
    slug: 'systems',
    question: 'How do your systems create results?',
    loomSrc: 'https://www.loom.com/embed/84c7db09042e4b539c9c173719e9899f',
    paddingBottom: '56.25%',
  },
  {
    slug: 'diy',
    question: 'Why can\'t I do it myself?',
    loomSrc: 'https://www.loom.com/embed/a42b886ac45d4befa5eb0d6c7fa95ff2',
    paddingBottom: '56.25%',
  },
  {
    slug: 'support',
    question: 'How do you support me daily?',
    loomSrc: 'https://www.loom.com/embed/9dd317fb87bb4f8e88963f6b3c3386f7',
    paddingBottom: '56.25%',
  },
];

const Confirmation = () => {
  const navigate = useNavigate();
  const [zoomed, setZoomed] = useState(null);

  // Deep-link support: /confirmation#results scrolls to that FAQ video and auto-plays it.
  const [targetSlug] = useState(() =>
    typeof window === 'undefined' ? null : window.location.hash.replace('#', '') || null
  );

  useEffect(() => {
    if (!targetSlug) return;
    const el = document.getElementById(targetSlug);
    if (!el) return;
    const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    return () => clearTimeout(t);
  }, [targetSlug]);

  // NOTE: The 'Schedule' Pixel event is intentionally NOT fired here anymore.
  // Bookings are now tracked authoritatively by the Calendly webhook
  // (/api/calendly-webhook → Meta CAPI), which catches every booking including
  // those made inside the Typeform. Firing here too would double-count.

  return (
    <div className="min-h-screen bg-[#2C0616]" data-testid="confirmation-page">
      {/* Slim header */}
      <div className="bg-[#2C0616] py-3 md:py-4 px-4 md:px-6 border-b-2 border-[#C9A55A]/35">
        <div className="max-w-7xl mx-auto flex items-center justify-center md:justify-start">
          <a href="/" className="block" data-testid="header-logo">
            <BrandMark size="sm" testId="header-logo-mark" />
          </a>
        </div>
      </div>

      {/* HERO — compact pill + tight headline */}
      <section className="px-4 md:px-6 pt-8 md:pt-12 pb-6 md:pb-8 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-[#220410] border border-[#C9A55A]/35 text-[#C9A55A] rounded-full px-4 py-1.5 md:px-5 md:py-2 mb-5 md:mb-6">
          <CheckCircle className="w-4 h-4 md:w-5 md:h-5" strokeWidth={2.5} />
          <span
            className="font-sans text-[10px] md:text-xs tracking-[0.3em]"
            style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
          >
            You're Booked In
          </span>
        </div>

        <h1
          className="font-sans text-2xl md:text-4xl lg:text-5xl text-[#F5F0E8] leading-[1.1] mb-3 md:mb-4"
          style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
          data-testid="confirmation-headline"
        >
          Your Call Is Confirmed.<br />Watch This Before We Speak.
        </h1>

        <p
          className="font-sans text-sm md:text-base text-[#F5F0E8]/60 leading-relaxed max-w-xl mx-auto"
          data-testid="confirmation-subheadline"
        >
          Check your inbox for the calendar invite. Watch the short video below so you're ready
          to get the most out of our call.
        </p>
      </section>

      {/* MANDATORY VIDEO */}
      <section className="px-4 md:px-6 pb-8 md:pb-12" data-testid="mandatory-video-section">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-[16px] overflow-hidden border-2 md:border-4 border-[#C9A55A]/35 shadow-xl bg-[#220410]">
            <VidalyticsEmbed embedId="vidalytics_embed_YM6iDEtJGZvXPL33" testId="mandatory-video" />
          </div>
        </div>
      </section>

      {/* URGENCY VIDEO */}
      <section className="px-4 md:px-6 pb-10 md:pb-14" data-testid="urgency-video-section">
        <div className="max-w-4xl mx-auto">
          <h2
            className="text-center font-sans text-xl md:text-2xl lg:text-3xl text-[#F5F0E8] leading-tight mb-4 md:mb-6"
            style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
            data-testid="urgency-video-headline"
          >
            Watch this before our call!
          </h2>
          <div className="rounded-[16px] overflow-hidden border-2 md:border-4 border-[#C9A55A]/35 shadow-xl bg-[#220410]">
            <VidalyticsEmbed embedId="vidalytics_embed_kzhDOOofmQbxDeIT" testId="urgency-video" />
          </div>
        </div>
      </section>

      {/* FAQ VIDEO GRID */}
      <section className="px-4 md:px-6 pb-10 md:pb-16" data-testid="faq-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center space-y-1.5 mb-6 md:mb-10">
            <p
              className="font-sans text-[10px] md:text-xs tracking-[0.3em] text-[#F5F0E8]/38"
              style={{ fontWeight: 500 }}
            >
              FAQ
            </p>
            <h2
              className="font-sans text-xl md:text-2xl lg:text-3xl text-[#F5F0E8] leading-tight"
              style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
            >
              Everything You Might Be Wondering
            </h2>
            <p className="font-sans text-xs md:text-sm text-[#F5F0E8]/38 max-w-md mx-auto pt-0.5">
              Quick answers to the questions creators ask most before our call.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-10 md:gap-x-10 md:gap-y-14">
            {FAQ_ITEMS.map((item, i) => (
              <FaqTile
                key={item.question}
                index={i + 1}
                question={item.question}
                src={item.src}
                loomSrc={item.loomSrc}
                paddingBottom={item.paddingBottom}
                slug={item.slug}
                autoOpen={targetSlug === item.slug}
              />
            ))}
          </div>
        </div>
      </section>

      {/* PROOF — Gry testimonial + real earnings screenshots */}
      <section className="px-4 md:px-6 pb-10 md:pb-16" data-testid="proof-section">
        <div className="max-w-5xl mx-auto space-y-10 md:space-y-14">
          {/* Gry testimonial video */}
          <div className="max-w-4xl mx-auto space-y-4" data-testid="gry-testimonial-section">
            <div className="text-center space-y-1">
              <p
                className="font-sans text-[10px] md:text-xs tracking-[0.3em] text-[#F5F0E8]/38"
                style={{ fontWeight: 500 }}
              >
                Real Case Study
              </p>
              <h2
                className="font-sans text-xl md:text-2xl lg:text-3xl text-[#F5F0E8] leading-tight"
                style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
              >
                How we took Gry from $900 to $80,000/month in 5 months
              </h2>
            </div>
            <VideoCard loomSrc="https://www.loom.com/embed/b0d62d67819345e7ada03bc416089009" testId="gry-testimonial-video" />
          </div>

          {/* Real Results dashboards (mirrors landing page) */}
          <div className="space-y-6 md:space-y-8">
            <div className="text-center space-y-3">
              <h2
                className="font-sans text-xl md:text-3xl lg:text-4xl text-[#F5F0E8] leading-[1.1]"
                style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
                data-testid="results-headline"
              >
                From $1K to $80K/month<br className="hidden md:block" />
                <span className="block md:inline"> in 5 months.</span>
              </h2>

              <div className="flex items-center justify-center gap-3" data-testid="creator-avatar-row">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border-2 border-[#C9A55A]/35 shrink-0 shadow-md">
                  <img
                    src="/screenshots/creator-photo.png"
                    alt="Prima Agency creator"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '50% 30%' }}
                    loading="lazy"
                  />
                </div>
                <p className="font-sans text-xs md:text-sm text-[#F5F0E8]/60 italic">
                  Transparent creator transformation
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="dashboards">
              {/* BEFORE */}
              <figure
                className="relative bg-white border-2 border-[#C9A55A]/15 rounded-[16px] overflow-hidden shadow-md"
                data-testid="before-shot"
              >
                <div className="flex items-center justify-between px-4 py-3 bg-[#2C0616] border-b border-[#C9A55A]/15">
                  <span
                    className="font-sans text-[10px] md:text-xs tracking-[0.2em] text-[#F5F0E8]/38"
                    style={{ fontWeight: 500 }}
                  >
                    Before · Month 1
                  </span>
                  <span className="font-sans text-sm md:text-base text-[#F5F0E8]/55" style={{ fontWeight: 500 }}>
                    $951
                  </span>
                </div>
                <img
                  src="/screenshots/gry-before.webp"
                  alt="Creator dashboard — Month 1 earnings"
                  onClick={() => setZoomed({ src: '/screenshots/gry-before.webp', alt: 'Creator dashboard — Month 1 earnings' })}
                  className="w-full h-auto block cursor-zoom-in"
                  loading="lazy"
                  />
              </figure>

              {/* AFTER */}
              <figure
                className="relative bg-white border-4 border-[#C9A55A]/35 rounded-[16px] overflow-hidden shadow-2xl"
                data-testid="after-shot"
              >
                <div className="flex items-center justify-between px-4 py-3 bg-[#220410]">
                  <span
                    className="font-sans text-[11px] md:text-[13px] tracking-[0.18em] text-[#E0C07A]"
                    style={{ fontWeight: 700 }}
                  >
                    After · Month 5
                  </span>
                  <span className="prima-glow font-sans text-base md:text-lg" style={{ fontWeight: 800 }}>
                    $79,446
                  </span>
                </div>
                <img
                  src="/screenshots/gry-after.webp"
                  alt="Creator dashboard — Month 5 earnings"
                  onClick={() => setZoomed({ src: '/screenshots/gry-after.webp', alt: 'Creator dashboard — Month 5 earnings' })}
                  className="w-full h-auto block cursor-zoom-in"
                  loading="lazy"
                  />
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* GUARANTEE — moved to bottom */}
      <section className="px-6 py-12 md:py-16 bg-[#220410] text-[#F5F0E8]" data-testid="guarantee-section">
        <div className="max-w-4xl mx-auto text-center space-y-5 md:space-y-6">
          <div className="flex justify-center">
            <div className="bg-[#220410] border border-[#C9A55A]/35 rounded-full p-3 md:p-4">
              <ShieldCheck className="w-8 h-8 md:w-12 md:h-12 text-[#C9A55A]" strokeWidth={1.75} />
            </div>
          </div>

          <p
            className="font-sans text-[11px] md:text-[13px] tracking-[0.18em] text-[#E0C07A]"
            style={{ fontWeight: 700 }}
            data-testid="guarantee-eyebrow"
          >
            Our Guarantee To You
          </p>

          <h2
            className="font-sans text-2xl md:text-4xl lg:text-5xl leading-[1.1]"
            style={{ fontWeight: 800, letterSpacing: '-0.015em' }}
            data-testid="guarantee-headline"
          >
            60 Days — Double Or Nothing.
          </h2>

          <p
            className="font-sans text-sm md:text-base leading-relaxed text-[#F5F0E8]/85 max-w-xl mx-auto"
            data-testid="guarantee-body"
          >
            If we don't double your earnings within 60 days of onboarding, we work the third month
            for free — until we do.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C0616] py-6 md:py-8 px-6 border-t-2 border-[#C9A55A]/15">
        <div className="max-w-7xl mx-auto text-center space-y-2">
          <p className="font-sans text-sm md:text-base text-[#F5F0E8]/75">
            See you on the call — <span style={{ fontWeight: 800, letterSpacing: '-0.015em' }}>Frederik &amp; Joachim</span>
          </p>
          <button
            onClick={() => navigate('/')}
            className="text-[#F5F0E8]/38 font-sans text-xs hover:text-[#F5F0E8] hover:underline"
          >
            ← Back to Home
          </button>
        </div>
      </footer>
      {zoomed && (
        <ImageLightbox
          src={zoomed.src}
          alt={zoomed.alt}
          label={zoomed.alt}
          onClose={() => setZoomed(null)}
        />
      )}
    </div>
  );
};

export default Confirmation;
